import type { IconProps } from '@common/Icon/Icon';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';

import type { CSSProperties } from 'react';
import { useEffect, useRef } from 'react';

export enum CollapsibleTheme {
  DARK,
  LIGHT,
}
export enum CollapsibleBackgroundTheme {
  NONE,
  LIGHT,
}
export interface CollapsibleProps {
  title: React.ReactNode;
  isOpen?: boolean;
  onOpen?: (newState: boolean) => void;
  openIcon: React.ReactElement<IconProps>;
  closeIcon: React.ReactElement<IconProps>;
  theme?: CollapsibleTheme;
  children?: React.ReactNode;
  backgroundTheme?: CollapsibleBackgroundTheme;
  disableScrolling?: boolean;
  styles?: CSSProperties;
}

const ANIMATION_DURATION = 200;

export const BaseCollapsible = ({
  isOpen = false,
  onOpen,
  children,
  title,
  openIcon,
  closeIcon,
  theme = CollapsibleTheme.DARK,
  backgroundTheme = CollapsibleBackgroundTheme.NONE,
  disableScrolling = false,
  styles = {},
}: CollapsibleProps) => {
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (!isOpen || disableScrolling) {
      return;
    }

    setTimeout(() => {
      // animation callbacks were to early so we wait another 50ms before scrolling to the element
      const currentElement = listRef.current?.children[0];
      if (currentElement && currentElement.scrollIntoView) {
        currentElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, ANIMATION_DURATION + 50);
  }, [isOpen, disableScrolling]);

  return (
    <ul className="border-black mb-8 mt-8" ref={listRef} data-testid="collapsible-container">
      <li className="w-full border-t border-white">
        <button
          type="button"
          data-testid="collapsible-button"
          onClick={() => onOpen?.(!isOpen)}
          className={classNames(
            'flex',
            'w-full',
            'py-8',
            'px-6',
            'text-left',
            'text-2xl',
            'outline-none',
            'focus:outline-none',
            'justify-between',
            'cursor-pointer',
            theme === CollapsibleTheme.DARK && 'text-gray-0 bg-gray-500',
            theme === CollapsibleTheme.LIGHT && 'text-gray-800 bg-gray-50'
          )}
          style={styles}
        >
          {title}
          <span>{isOpen ? openIcon : closeIcon}</span>
        </button>
        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              key="content"
              initial="collapsed"
              animate="open"
              exit="collapsed"
              variants={{
                open: { opacity: 1, height: 'auto' },
                collapsed: { opacity: 0, height: 0 },
              }}
              transition={{ duration: ANIMATION_DURATION / 1000 }}
              data-testid="collapsible-content"
              className={classNames(
                'overflow-x-visible',
                backgroundTheme === CollapsibleBackgroundTheme.LIGHT && 'bg-gray-50'
              )}
            >
              {children}
            </motion.div>
          )}
        </AnimatePresence>
      </li>
    </ul>
  );
};
